import { useMemo } from 'react';
import configurationApi from '../services/configurationApi';

/**
 * Resolved Piper capability flags.
 *
 * Combines tenant feature flags with GET /configuration/piper-access
 * (tenant + broker + user consent). Capability booleans are ANDed with
 * `effectiveEnabled`, so call sites never need a second consent check for
 * actual AI calls. `enabled` stays true when consent is still required so
 * the rail can show and prompt; it is false after a decline.
 */
export default function usePiperFeatures() {
  const { data: featureFlags = {} } = configurationApi.endpoints.getFeatureFlags.useQuery();
  const { data: access, isLoading: accessLoading, isFetching: accessFetching } = configurationApi.endpoints.getPiperAccess.useQuery();

  return useMemo(() => {
    const master = featureFlags.piperAIEnabled === true;
    const reason = access?.reason ?? null;
    const consentRequired = reason === 'consent_required' || reason === 'terms_updated';
    const declined = reason === 'consent_declined';
    const effectiveEnabled = access?.effectiveEnabled === true;

    let enabled;
    if (!access && (accessLoading || accessFetching)) {
      enabled = master;
    } else if (access) {
      const tenantOn = access.tenantEnabled === true;
      const brokerOn = access.brokerEnabled === true;
      enabled = tenantOn && brokerOn && reason !== 'consent_declined';
    } else {
      enabled = master;
    }

    const on = (key) => master && featureFlags[key] === true && effectiveEnabled;

    return {
      enabled,
      consentRequired,
      declined,
      effectiveEnabled,
      access: access ?? null,
      chat: on('piperChatEnabled'),
      chatQuickActions: on('piperChatQuickActionsEnabled'),
      chatScenarios: on('piperChatScenariosEnabled'),
      chatFeedback: on('piperChatFeedbackEnabled'),
      pricingInsights: on('piperPricingInsightsEnabled'),
      scenarioChat: on('piperScenarioChatEnabled'),
    };
  }, [featureFlags, access, accessLoading, accessFetching]);
}
